import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import InputMask from "react-input-mask";
import { useNavigate } from "react-router-dom";
import {lazy, useEffect, useState} from "react";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import defaultEmployeeImage from "../../../../../assets/svg/defaultUser.svg";
import ComercialDrawer from "../../../../../components/pet-beauty/CormercialDrawer";
import ComercialInterfaceHeader from "../../../../../components/comercial/ComercialInterfaceHeader";
import { IStore } from "../../../../../services/api/Store/types";
import { PrimaryButton } from "../../../../../components/general/buttons/PrimaryButton";
import { SecondaryButton } from "../../../../../components/general/buttons/SecondaryButton";
import { getLocalStorageItem } from "../../../../../hooks/localStorageHooks";
import WorkingScaleSelector from "../../../../../components/comercial/WorkingScaleSelector";
import { DocType } from "../../../../../types/types";
import useSearchUserByDocNumber from "../../../../../hooks/search/useSearchUserByDocNumber";
import employeeApi from "../../../../../services/api/Employee";
import { IEmployee } from "../../../../../services/api/Employee/types";
import { sharedStyles } from "../../../../../styles/shared";
import BackPageButton, {
  LeftMarginBackButton,
} from "../../../../../components/general/buttons/BackPageButton";
const CustomCard = lazy(
  () => import("../../../../../components/general/CustomCard")
);
import { useTranslation } from "react-i18next";
import imagesApi from "@/services/api/Image";
import {IImage} from "@/services/api/Image/types";

const AddEmployee = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const selectedStore: IStore = getLocalStorageItem("selectedStore");
  const [foundedUserImage, setFoundedUserImage] = useState<IImage | null>(null);
  const {
    foundedUser,
    docNumber,
    setDocNumber,
    searchingUser,
    setSearchingUser,
  } = useSearchUserByDocNumber(DocType.CPF);

    const detectBestInterval = (store: IStore) => {
        const allTimes = [
            ...(store?.timeMonday || []),
            ...(store?.timeTuesday || []),
            ...(store?.timeWednesday || []),
            ...(store?.timeThursday || []),
            ...(store?.timeFriday || []),
            ...(store?.timeSaturday || []),
            ...(store?.timeSunday || []),
        ];
        if (allTimes.some((t) => t.endsWith(":15") || t.endsWith(":45"))) return 15;
        if (allTimes.some((t) => t.endsWith(":30"))) return 30;
        return 60;
    };

    const [intervalMinutes, setIntervalMinutes] = useState<number>(() =>
        detectBestInterval(selectedStore)
    );

  const [timeMonday, setTimeMonday] = useState<string[]>(
    selectedStore?.timeMonday
  );
  const [timeTuesday, setTimeTuesday] = useState<string[]>(
    selectedStore?.timeTuesday
  );
  const [timeWednesday, setTimeWednesday] = useState<string[]>(
    selectedStore?.timeWednesday
  );
  const [timeThursday, setTimeThursday] = useState<string[]>(
    selectedStore?.timeThursday
  );
  const [timeFriday, setTimeFriday] = useState<string[]>(
    selectedStore?.timeFriday
  );
  const [timeSaturday, setTimeSaturday] = useState<string[]>(
    selectedStore?.timeSaturday
  );
  const [timeSunday, setTimeSunday] = useState<string[]>(
    selectedStore?.timeSunday
  );

  const handleUserAndEmployeeCreation = async () => {
    if (!foundedUser) return;
    setSearchingUser(true);
    try {
    await employeeApi.createEmployee({
        userId: foundedUser.id,
        storeId: selectedStore.id,
        timeMonday,
        timeTuesday,
        timeWednesday,
        timeThursday,
        timeFriday,
        timeSaturday,
        timeSunday,
      } as IEmployee);
      navigate("/employees");
    } catch (error) {
      alert(t("employeesPages.add.error"));
      console.warn("Error creating a employee: ", error);
    }
    setSearchingUser(false);
  };

   useEffect(() => {
        if (!foundedUser?.id) return;
        imagesApi.findByUserId(foundedUser.id).then(setFoundedUserImage);
    }, [foundedUser]);

  const CpfField = (
    <InputMask
      mask="999.999.999-99"
      disabled={false}
      maskChar="_"
      value={docNumber}
      onChange={(e) => setDocNumber(e.target.value)}
    >
      <TextField
        label="CPF*"
        fullWidth
        size="small"
        sx={{ maxWidth: "400px" }}
      />
    </InputMask>
  );

  const renderEmployeeData = (
    <>
      <ComercialInterfaceHeader title={t("employeesPages.add.searchTitle")} />

      <Box sx={{ ...sharedStyles.verticalContent, alignItems: "center" }}>
        <Box
          sx={{
            ...sharedStyles.verticalContent,
            gap: "10px",
            width: "100%",
            alignItems: "center",
          }}
        >
          {CpfField}
          {!foundedUser && (
            <Button
              variant="text"
              onClick={() => navigate("/register")}
              sx={{ textTransform: "none" }}
            >
                {t("employeesPages.add.notFoundAction")}
            </Button>
          )}
        </Box>

        <Box
          sx={{
            ...sharedStyles.verticalContent,
            marginTop: "10px",
            gap: "10px",
            width: "100%",
            alignItems: "center",
          }}
        >
          {!searchingUser ? (
            <>
              {foundedUser && (
                <>
                  <Typography fontWeight="bold">
                    {t("employeesPages.add.foundLabel")}
                  </Typography>
                <CustomCard
                  photoURL={foundedUserImage?.imageUrl}
                  defaultPhoto={defaultEmployeeImage}
                  header={foundedUser.name}
                  primaryText={foundedUser.docNumber}
                  secondaryText={""}
                  isSelected
                />
                </>
              )}
            </>
          ) : (
            <>
              <CircularProgress />
              <Typography color="text.secondary">
                {t("employeesPages.add.searching")}
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </>
  );

  const renderWorkScaleSelector = (
    <>
      <ComercialInterfaceHeader title={t("employeesPages.add.scaleTitle")} />
      <Box sx={sharedStyles.verticalContent}>
        <WorkingScaleSelector
          intervalMinutes={intervalMinutes}
          setIntervalMinutes={setIntervalMinutes}
          timeMonday={timeMonday}
          setTimeMonday={setTimeMonday}
          timeTuesday={timeTuesday}
          setTimeTuesday={setTimeTuesday}
          timeWednesday={timeWednesday}
          setTimeWednesday={setTimeWednesday}
          timeThursday={timeThursday}
          setTimeThursday={setTimeThursday}
          timeFriday={timeFriday}
          setTimeFriday={setTimeFriday}
          timeSaturday={timeSaturday}
          setTimeSaturday={setTimeSaturday}
          timeSunday={timeSunday}
          setTimeSunday={setTimeSunday}
        />
      </Box>
    </>
  );

  const renderOptionButtons = (
    <Box sx={sharedStyles.dividedFormContainer}>
      <PrimaryButton
        label={t("employeesPages.add.confirm")}
        onClickAction={handleUserAndEmployeeCreation}
        icon={<PersonAddIcon />}
        isLoading={searchingUser}
        isDisabled={searchingUser || !foundedUser}
      />
      <SecondaryButton
        label={t("employeesPages.add.cancel")}
        isDisabled={searchingUser}
        onClickAction={() => navigate("/employees")}
      />
    </Box>
  );
  const renderAddPageContent = (
    <>
      <LeftMarginBackButton />

      <form style={sharedStyles.form}>
        {renderEmployeeData}
        {renderWorkScaleSelector}
        {renderOptionButtons}
      </form>
    </>
  );

return (
    <Box sx={sharedStyles.sharedMainContainer}>
    <ComercialDrawer content={renderAddPageContent} />
    </Box>
);
};

export default AddEmployee;
